import { useState, useCallback, useMemo, useEffect } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type {
  FamilyMemberHistory,
  Bundle,
  Resource,
} from '@medplum/fhirtypes';
import { OperationOutcomeError } from '@medplum/core';
import { v4 as uuidv4 } from 'uuid';
import type {
  FamilyHistoryData,
  FamilyMemberLocal,
  FamilyConditionLocal,
  FamilyRelationship,
} from '../types/familyHistory';
import {
  RELATIONSHIP_CODES,
  RELATIONSHIP_CODE_TO_TYPE,
  RELATIONSHIP_SYSTEM,
  FAMILY_HISTORY_STATUS,
} from '../types/familyHistory';
import { onboardingService, type FamilyHistoryReviewed } from '../services/onboardingService';

// Helper: Extract relationship from FHIR coding
export function extractRelationship(fmh: FamilyMemberHistory): FamilyRelationship | null {
  const coding = fmh.relationship?.coding?.[0];
  if (coding?.code) {
    return RELATIONSHIP_CODE_TO_TYPE[coding.code] || null;
  }
  return null;
}

// Helper: Extract conditions from FamilyMemberHistory
export function extractConditions(fmh: FamilyMemberHistory): FamilyConditionLocal[] {
  if (!fmh.condition) return [];

  return fmh.condition.map((cond) => {
    // Try to get age at onset from various fields
    let ageAtOnset: string | undefined;
    if (cond.onsetAge?.value !== undefined) {
      ageAtOnset = String(cond.onsetAge.value);
    } else if (cond.onsetString) {
      ageAtOnset = cond.onsetString;
    }

    return {
      id: uuidv4(),
      conditionName: cond.code?.text || cond.code?.coding?.[0]?.display || '',
      ageAtOnset,
    };
  });
}

// Helper: Extract age from FamilyMemberHistory
export function extractAge(fmh: FamilyMemberHistory): string | undefined {
  if (fmh.ageAge?.value !== undefined) {
    return String(fmh.ageAge.value);
  }
  if (fmh.ageString) {
    return fmh.ageString;
  }
  return undefined;
}

// Helper: Check if deceased
export function extractDeceased(fmh: FamilyMemberHistory): { isDeceased: boolean; causeOfDeath?: string } {
  // Check deceasedBoolean
  if (fmh.deceasedBoolean === true) {
    return { isDeceased: true };
  }
  // Check deceasedAge or deceasedString (implies deceased)
  if (fmh.deceasedAge || fmh.deceasedString) {
    return { isDeceased: true };
  }
  return { isDeceased: false };
}

// Extract FamilyHistoryData from FHIR resources
export function extractFamilyHistoryData(familyMemberHistories: FamilyMemberHistory[]): FamilyHistoryData {
  const familyMembers: FamilyMemberLocal[] = [];

  for (const fmh of familyMemberHistories) {
    const relationship = extractRelationship(fmh);
    if (!relationship) continue; // Skip if we can't determine relationship

    const { isDeceased } = extractDeceased(fmh);
    const conditions = extractConditions(fmh);
    const notes = fmh.note?.[0]?.text;

    // Extract cause of death from note or extension
    let causeOfDeath: string | undefined;
    if (isDeceased && notes) {
      const causeMatch = notes.match(/Cause of death:\s*(.+?)(?:\.|$)/i);
      if (causeMatch) {
        causeOfDeath = causeMatch[1].trim();
      }
    }

    // Filter out cause of death from notes if it was extracted
    let cleanNotes = notes;
    if (causeOfDeath && cleanNotes) {
      cleanNotes = cleanNotes.replace(/Cause of death:\s*[^.]+\.?\s*/i, '').trim() || undefined;
    }

    familyMembers.push({
      localId: uuidv4(),
      fhirId: fmh.id,
      relationship,
      name: fmh.name,
      isDeceased,
      ageOrAgeAtDeath: extractAge(fmh),
      causeOfDeath,
      conditions,
      notes: cleanNotes,
    });
  }

  return { familyMembers };
}

// Build FHIR FamilyMemberHistory from local data
export function buildFamilyMemberResource(
  local: FamilyMemberLocal,
  patientId: string
): FamilyMemberHistory {
  const relationshipInfo = RELATIONSHIP_CODES[local.relationship];

  const familyMemberHistory: FamilyMemberHistory = {
    resourceType: 'FamilyMemberHistory',
    ...(local.fhirId && { id: local.fhirId }),
    patient: { reference: `Patient/${patientId}` },
    status: FAMILY_HISTORY_STATUS.completed,
    relationship: {
      coding: [
        {
          system: RELATIONSHIP_SYSTEM,
          code: relationshipInfo.code,
          display: relationshipInfo.display,
        },
      ],
      text: relationshipInfo.display,
    },
  };

  // Add name if provided
  if (local.name) {
    familyMemberHistory.name = local.name;
  }

  // Add age (using ageString for flexibility)
  if (local.ageOrAgeAtDeath) {
    // Try to parse as number for ageAge, otherwise use ageString
    const ageNum = parseInt(local.ageOrAgeAtDeath, 10);
    if (!isNaN(ageNum) && ageNum > 0 && ageNum < 150) {
      familyMemberHistory.ageAge = { value: ageNum, unit: 'years', system: 'http://unitsofmeasure.org', code: 'a' };
    } else {
      familyMemberHistory.ageString = local.ageOrAgeAtDeath;
    }
  }

  // Add deceased status
  if (local.isDeceased) {
    familyMemberHistory.deceasedBoolean = true;
  }

  // Add conditions
  if (local.conditions.length > 0) {
    familyMemberHistory.condition = local.conditions
      .filter((c) => c.conditionName.trim())
      .map((cond) => {
        const fhirCondition: NonNullable<FamilyMemberHistory['condition']>[number] = {
          code: {
            text: cond.conditionName,
          },
        };

        // Add onset age if provided
        if (cond.ageAtOnset) {
          const onsetNum = parseInt(cond.ageAtOnset, 10);
          if (!isNaN(onsetNum) && onsetNum > 0 && onsetNum < 150) {
            fhirCondition.onsetAge = { value: onsetNum, unit: 'years', system: 'http://unitsofmeasure.org', code: 'a' };
          } else {
            fhirCondition.onsetString = cond.ageAtOnset;
          }
        }

        return fhirCondition;
      });
  }

  // Build note text (combining cause of death and additional notes)
  const noteParts: string[] = [];
  if (local.isDeceased && local.causeOfDeath) {
    noteParts.push(`Cause of death: ${local.causeOfDeath}`);
  }
  if (local.notes) {
    noteParts.push(local.notes);
  }
  if (noteParts.length > 0) {
    familyMemberHistory.note = [{ text: noteParts.join('. ') }];
  }

  return familyMemberHistory;
}

export function useFamilyHistory() {
  const medplum = useMedplum();
  const baseProfile = useMedplumProfile() as Resource | undefined;

  const [familyMemberHistories, setFamilyMemberHistories] = useState<FamilyMemberHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewedFlags, setReviewedFlags] = useState<FamilyHistoryReviewed>(
    onboardingService.getFamilyHistoryReviewed()
  );

  const patientId = baseProfile?.id;
  const resourceType = (baseProfile as { resourceType?: string })?.resourceType;

  // Load existing data from Medplum
  const loadData = useCallback(async () => {
    if (!patientId || resourceType !== 'Patient') {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch FamilyMemberHistory resources
      try {
        const result = await medplum.search('FamilyMemberHistory', {
          patient: `Patient/${patientId}`,
          _sort: '-_lastUpdated',
        });
        const bundle = result as Bundle<FamilyMemberHistory>;
        setFamilyMemberHistories(
          bundle.entry?.map((e) => e.resource as FamilyMemberHistory) || []
        );
      } catch {
        // No family history found - that's okay
        setFamilyMemberHistories([]);
      }
    } catch (err) {
      console.error('Failed to load family history data:', err);
      if (err instanceof OperationOutcomeError) {
        console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
      }
      setError('Unable to load family history. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [medplum, patientId, resourceType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Extract to local format
  const familyHistoryData = useMemo(
    () => extractFamilyHistoryData(familyMemberHistories),
    [familyMemberHistories]
  );

  // Data status
  const hasFamilyMembers = familyHistoryData.familyMembers.length > 0;

  // Section is complete if: has data OR marked as "no content"
  const isComplete = hasFamilyMembers || reviewedFlags.noFamilyHistory;

  // Update reviewed flags
  const setReviewedFlag = useCallback((flag: keyof FamilyHistoryReviewed, value: boolean) => {
    const updated = onboardingService.setFamilyHistoryReviewed({ [flag]: value });
    setReviewedFlags(updated);
  }, []);

  // Save all changes
  const saveAll = useCallback(
    async (data: FamilyHistoryData): Promise<boolean> => {
      if (!patientId) {
        setError('Patient data not loaded');
        return false;
      }

      // Validate required fields
      for (const member of data.familyMembers) {
        if (!member.isDeleted && !member.relationship) {
          setError('Please select a relationship for all family members.');
          return false;
        }
      }

      try {
        setIsSaving(true);
        setError(null);

        const operations: Promise<unknown>[] = [];

        // Save family members
        for (const member of data.familyMembers) {
          if (member.isDeleted && member.fhirId) {
            await medplum.deleteResource('FamilyMemberHistory', member.fhirId);
          } else if (!member.isDeleted && member.relationship) {
            const resource = buildFamilyMemberResource(member, patientId);
            if (member.fhirId) {
              operations.push(medplum.updateResource(resource));
            } else {
              operations.push(medplum.createResource(resource));
            }
          }
        }

        await Promise.all(operations);

        // Reload data to get updated IDs
        await loadData();
        return true;
      } catch (err) {
        console.error('Failed to save family history data:', err);
        if (err instanceof OperationOutcomeError) {
          console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
        }
        setError('Unable to save. Please try again.');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [medplum, patientId, loadData]
  );

  return {
    familyHistoryData,
    isLoading,
    isSaving,
    error,
    isComplete,
    hasFamilyMembers,
    // Reviewed flags
    reviewedFlags,
    setReviewedFlag,
    // Actions
    saveAll,
    refresh: loadData,
  };
}
